/**
 * Team-management API client (PR F).
 *
 * Backs the new Team tab on ProjectPage: invite-link minting, members
 * list, member removal, and contact-attach (the post-creation
 * equivalent of the project-create FSM's client step).
 */
import { request } from "./client";
import type {
  ContactCreateRequest,
  InviteRequest,
  InviteResponse,
  ProjectMemberRow,
} from "../lib/types";

/** Read-only members list — Mini App refetches this after a remove
 *  rather than re-pulling the whole ProjectDetail blob. */
export async function listProjectMembers(
  projectId: string,
  signal?: AbortSignal,
): Promise<ProjectMemberRow[]> {
  return request<ProjectMemberRow[]>(
    `/api/v1/projects/${encodeURIComponent(projectId)}/members`,
    { method: "GET", signal },
  );
}

/** Hard-delete a member row. Backend returns 204 on success. */
export async function removeProjectMember(
  projectId: string,
  memberId: string,
): Promise<void> {
  await request<null>(
    `/api/v1/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(memberId)}`,
    { method: "DELETE" },
  );
}

/** Mint a fresh invite link. The backend builds the t.me URL via
 *  `bot.get_me()` so we don't have to know the bot's username on the
 *  client. */
export async function createProjectInvite(
  projectId: string,
  body: InviteRequest = {},
): Promise<InviteResponse> {
  return request<InviteResponse>(
    `/api/v1/projects/${encodeURIComponent(projectId)}/invites`,
    { method: "POST", body },
  );
}

/** Upsert a Contact in the caller's address book + attach as a
 *  member. Either `telegram_user_id` or `telegram_username` must be
 *  supplied (backend rejects both-null with 400). */
export async function attachContactToProject(
  projectId: string,
  body: ContactCreateRequest,
): Promise<ProjectMemberRow> {
  return request<ProjectMemberRow>(
    `/api/v1/projects/${encodeURIComponent(projectId)}/contacts`,
    { method: "POST", body },
  );
}
